/**
 * Piezas comunes de las vistas previas de los libros fiscales (pedido del usuario del 2026-10-30): la tarjeta «Así queda
 * en los libros fiscales», sus secciones por libro y sus filas. Solo muestran: cada pantalla pasa los montos que el
 * servidor ya calculó o guardó, y su prueba de API comprueba que el libro dice lo mismo después de guardar.
 * La usan la compra (`compras/VistaLibros.tsx`), los ajustes, la numeración de comprobantes y las devoluciones.
 */
import type { ReactNode } from 'react';
import { Dinero } from '@/componentes/Dinero.tsx';
import { Badge } from '@/componentes/ui/badge.tsx';

const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
/** «octubre 2026» de una fecha AAAA-MM-DD. */
export const mesDeLaFecha = (fecha: string) => `${MESES[Number(fecha.slice(5, 7)) - 1] ?? fecha.slice(5, 7)} ${fecha.slice(0, 4)}`;
/** «octubre 2026» de un período AAAAMM. */
export const mesDelPeriodo = (periodo: string) => mesDeLaFecha(`${periodo.slice(0, 4)}-${periodo.slice(4, 6)}-01`);
/** Hoy en Caracas (AAAA-MM-DD). */
export const hoyCaracas = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Caracas' }).format(new Date());

/** Una fila «etiqueta … valor»; el valor largo baja de línea sin ensanchar la columna. */
export function FilaLibro({ etiqueta, children, testid }: { etiqueta: string; children: ReactNode; testid?: string | undefined }) {
  return (
    <p className="flex justify-between gap-3">
      <span className="shrink-0 text-muted-foreground">{etiqueta}</span>
      <span className="min-w-0 break-words text-right" data-testid={testid}>{children}</span>
    </p>
  );
}

/** Un monto en Bs, como en los libros. */
export const BsLibro = ({ valor }: { valor: string }) => <Dinero valor={valor} moneda="BS" modo="bs" />;

/** Título de una sección (un libro) con su nota pequeña debajo. */
export function TituloLibro({ titulo, nota, testid }: { titulo: string; nota?: ReactNode; testid?: string | undefined }) {
  return (
    <>
      <p className="text-xs font-semibold uppercase text-muted-foreground" data-testid={testid}>{titulo}</p>
      {nota ? <p className="text-[11px] text-muted-foreground">{nota}</p> : null}
    </>
  );
}

/** Aviso en ámbar dentro de la tarjeta (lo que todavía no entra al libro, o entra en otro mes). */
export const AvisoLibro = ({ children, testid }: { children: ReactNode; testid?: string | undefined }) => (
  <p className="rounded-md bg-amber-50 px-2 py-1 text-[11px] text-amber-900" data-testid={testid}>{children}</p>
);

/** La tarjeta «Así queda en los libros fiscales» con la marca «Vista previa». */
export function TarjetaVistaLibros({ testid, titulo = 'Así queda en los libros fiscales', children }: { testid: string; titulo?: string; children: ReactNode }) {
  return (
    <div className="space-y-2 rounded-2xl border bg-card p-3 text-sm" data-testid={testid}>
      <div className="flex items-center justify-between gap-2">
        <p className="font-medium">{titulo}</p>
        <Badge variant="info">Vista previa</Badge>
      </div>
      {children}
    </div>
  );
}
